<template>
    <div class="forum-table-page weeduc-admin-page text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage
                    :title="$t('admin.forum.title')"
                    :subtitle="$t('admin.forum.subtitleManage')"
                    :hasMargin="false"
                />
            </div>
            <div class="weeduc-admin-page__header-actions">
                <v-btn
                    variant="flat"
                    color="primary"
                    rounded="lg"
                    :title="$t('admin.forum.createTitle')"
                    :icon="$vuetify.display.xs"
                    class="text-none edit-courses-new-btn"
                    @click="newForumDialog = true"
                >
                    <template v-if="$vuetify.display.xs">
                        <v-icon>mdi-plus</v-icon>
                    </template>
                    <template v-else>
                        <v-icon class="me-1" size="small">mdi-plus</v-icon>
                        {{ $t('admin.forum.newForum') }}
                    </template>
                </v-btn>
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <div class="weeduc-admin-page__panel">
                <div class="weeduc-admin-page__toolbar">
                    <v-row align="end" dense>
                        <v-col cols="12" sm="8" md="6" lg="8">
                            <v-text-field
                                v-model="searchText"
                                :label="$t('common.search')"
                                :placeholder="$t('admin.forum.searchPlaceholder')"
                                class="inputd weeduc-admin-v-field"
                                variant="outlined"
                                density="compact"
                                hide-details
                                clearable
                                append-inner-icon="mdi-magnify"
                                @update:model-value="onSearch"
                            />
                        </v-col>
                        <v-col cols="12" sm="4" md="4" lg="4">
                            <v-select
                                v-model="statusFilter"
                                :items="statusFilterOptions"
                                item-title="text"
                                item-value="value"
                                :label="$t('common.status')"
                                variant="outlined"
                                class="inputd weeduc-admin-v-field"
                                :menu-props="adminSelectMenuProps"
                                @update:modelValue="filtrarForums"
                                density="compact"
                                hide-details
                            />
                        </v-col>
                    </v-row>
                </div>

                <div class="weeduc-admin-page__table-section">
                    <v-table class="custom-table">
                        <thead>
                            <tr>
                                <th class="text-center" style="width: 15%">
                                    ID
                                </th>
                                <th class="forum-table__title-column">
                                    {{ $t('common.title') }}
                                </th>
                                <th class="text-center">{{ $t('common.status') }}</th>
                                <th class="text-center">{{ $t('admin.forum.see') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="forum in paginatedForums"
                                :key="forum.id"
                            >
                                <td class="text-center">#{{ forum.id }}</td>
                                <td class="forum-table__title-column">
                                    {{ forum.title }}
                                </td>
                                <td class="text-center">
                                    <v-chip
                                        density="compact"
                                        :color="getStatusColor(forum.status)"
                                    >
                                        {{ getStatusText(forum.status) }}
                                    </v-chip>
                                </td>
                                <td class="text-center">
                                    <v-btn
                                        color="primary"
                                        variant="flat"
                                        size="small"
                                        rounded="lg"
                                        class="text-none forum-table__open"
                                        :title="$t('admin.forum.viewForum')"
                                        @click="editForum(forum.id, forum)"
                                    >
                                        {{ $t('admin.forum.viewForum') }}
                                    </v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </div>

                <v-responsive class="weeduc-admin-page__pagination">
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        :total-visible="5"
                        :disabled="!hasReady"
                        @update:model-value="onPageChange"
                    ></v-pagination>
                </v-responsive>

                <Loader v-if="!hasReady" />
            </div>
        </div>

        <!-- Create New Forum Dialog -->
        <v-dialog
            v-model="newForumDialog"
            max-width="520"
            content-class="forum-create-modal-overlay"
        >
            <div class="forum-create-modal">
                <header class="forum-create-modal__header">
                    <h3>{{ $t('admin.forum.createModalTitle') }}</h3>
                </header>

                <div class="forum-create-modal__body">
                    <v-text-field
                        v-model="newForumTitle"
                        :label="$t('admin.forum.forumTitleLabel')"
                        required
                        variant="outlined"
                        hide-details
                        density="compact"
                        class="mb-4"
                    />
                    <v-textarea
                        v-model="newForumDescription"
                        :label="$t('admin.forum.forumDescriptionLabel')"
                        required
                        auto-grow
                        rows="4"
                        variant="outlined"
                        hide-details
                        density="compact"
                    />
                </div>

                <footer class="forum-create-modal__footer">
                    <button
                        type="button"
                        class="forum-create-modal__btn forum-create-modal__btn--cancel"
                        :disabled="isCreatingForum"
                        @click="closeNewForumDialog"
                    >
                        {{ $t('common.cancel') }}
                    </button>
                    <button
                        type="button"
                        class="forum-create-modal__btn forum-create-modal__btn--create"
                        :disabled="!newForumTitle.trim() || isCreatingForum"
                        @click="createNewForum"
                    >
                        <span
                            v-if="isCreatingForum"
                            class="forum-create-modal__spinner"
                        />
                        {{ $t('common.create') }}
                    </button>
                </footer>
            </div>
        </v-dialog>
    </div>
</template>

<script>
    import Loader from '@/components/Loader/loader.vue';
    import TitlePage from '@/components/Weflix/titlePage.vue';
    import ApiForum from '@/utils/forum';
    import { debounce } from 'lodash';
    import { mapState, mapActions } from 'vuex';
    import { toast } from 'vue3-toastify';

    export default {
        components: {
            Loader,
            TitlePage,
        },
        data() {
            return {
                forumsData: [],
                searchText: '',
                currentPage: 1,
                itemsPerPage: 10,
                hasReady: false,
                isCreatingForum: false,
                statusFilter: 'todos',
                newForumDialog: false,
                newForumTitle: '',
                newForumDescription: '',
            };
        },
        computed: {
            ...mapState(['token', 'numberIntensTable', 'forum', 'dark']),

            adminSelectMenuProps() {
                return this.dark
                    ? { contentClass: 'weeduc-admin-select-menu-dark' }
                    : {};
            },

            statusFilterOptions() {
                return [
                    { text: this.$t('common.all'), value: 'todos' },
                    { text: this.$t('admin.forum.statusOpen'), value: 'aberto' },
                    { text: this.$t('admin.forum.statusClosed'), value: 'fechado' },
                ];
            },

            paginatedForums() {
                const startIndex = (this.currentPage - 1) * this.itemsPerPage;
                const endIndex = startIndex + this.itemsPerPage;
                return this.filteredForums.slice(startIndex, endIndex);
            },

            totalPages() {
                return Math.ceil(
                    this.filteredForums.length / this.itemsPerPage
                );
            },

            filteredForums() {
                let filtered = this.forumsData;

                if (this.searchText && this.searchText.trim() !== '') {
                    filtered = filtered.filter((forum) =>
                        forum.title
                            .toLowerCase()
                            .includes(this.searchText.toLowerCase())
                    );
                }

                if (this.statusFilter !== 'todos') {
                    filtered = filtered.filter((forum) => {
                        const isActive = forum.status === 1;
                        return this.statusFilter === 'aberto'
                            ? isActive
                            : !isActive;
                    });
                }

                return filtered;
            },
        },
        created() {
            this.getForumData();
        },
        methods: {
            ...mapActions(['storeForum']),

            getStatusColor(status) {
                return status === 1 ? 'success' : 'error';
            },

            getStatusText(status) {
                return status === 1
                    ? this.$t('admin.forum.statusOpen')
                    : this.$t('admin.forum.statusClosed');
            },

            filtrarForums() {
                this.currentPage = 1;
            },

            editForum(id, forum) {
                this.storeForum({ forum: forum });
                this.$router.push('/dashboard/forum/' + id);
            },

            closeNewForumDialog() {
                this.newForumDialog = false;
                this.newForumTitle = '';
                this.newForumDescription = '';
            },

            async getForumData() {
                try {
                    this.hasReady = false;
                    const response = await ApiForum.getDataForum(this.token);
                    this.forumsData = response;
                } catch (err) {
                    console.error('Erro ao carregar dados do Forum');
                    toast.error(this.$t('admin.forum.loadError'));
                } finally {
                    this.hasReady = true;
                }
            },

            async createNewForum() {
                if (!this.newForumTitle.trim()) {
                    toast.error(this.$t('common.required'));
                    return;
                }

                this.isCreatingForum = true;
                const payload = {
                    title: this.newForumTitle.trim(),
                    description: this.newForumDescription.trim(),
                    status: 1,
                };

                try {
                    const response = await ApiForum.createNewForum(
                        payload,
                        this.token
                    );
                    toast.success(this.$t('admin.forum.createSuccess'));
                    this.closeNewForumDialog();
                    this.getForumData();
                } catch (err) {
                    toast.error(this.$t('admin.forum.createError'));
                    console.error('Erro ao criar novo fórum:', err);
                } finally {
                    this.isCreatingForum = false;
                }
            },

            onSearch: debounce(function () {
                this.currentPage = 1;
            }, 300),

            onPageChange() {},
        },
    };
</script>

<style scoped>
    @import url('@/css/weeduc/admin/dashboard-weflix-shell.css');
    @import url('@/css/weeduc/admin/forum.css');
    .forum-table__title-column {
        width: 60%;
    }

    .forum-table__open {
        letter-spacing: 0;
        font-weight: 700;
    }

    .custom-table {
        margin-bottom: 1rem;
    }
</style>

<style>
    .forum-create-modal {
        background: #fff;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 12px 40px rgba(22, 2, 102, 0.18);
    }

    .forum-create-modal__header {
        padding: 20px 24px 8px;
    }

    .forum-create-modal__header h3 {
        margin: 0;
        font-size: 1.15rem;
        font-weight: 600;
        color: #1a1d26;
        line-height: 1.3;
    }

    .forum-create-modal__body {
        padding: 12px 24px 8px;
    }

    .forum-create-modal__footer {
        display: flex;
        gap: 10px;
        padding: 16px 24px 20px;
        border-top: 1px solid #eceef2;
        box-sizing: border-box;
    }

    .forum-create-modal__btn {
        box-sizing: border-box;
        width: 50%;
        flex: 1 1 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        height: 36px;
        min-height: 36px;
        padding: 0 12px;
        border: none;
        border-radius: 8px;
        font-size: 0.8125rem;
        font-weight: 600;
        letter-spacing: 0.02em;
        text-transform: none;
        cursor: pointer;
        transition: background-color 0.15s ease, opacity 0.15s ease;
    }

    .forum-create-modal__btn:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }

    .forum-create-modal__btn--cancel {
        color: #2a2f3a;
        background: #e8eaef;
    }

    .forum-create-modal__btn--cancel:hover:not(:disabled) {
        background: #dcdde3;
    }

    .forum-create-modal__btn--create {
        color: #fff !important;
        background: #160266 !important;
    }

    .forum-create-modal__btn--create:hover:not(:disabled) {
        color: #fff !important;
        background: #1f0a8a !important;
    }

    .forum-create-modal__btn--create:disabled {
        color: rgba(255, 255, 255, 0.75) !important;
        background: #160266 !important;
    }

    .forum-create-modal__spinner {
        width: 14px;
        height: 14px;
        border: 2px solid rgba(255, 255, 255, 0.35);
        border-top-color: #fff;
        border-radius: 50%;
        animation: forum-create-spin 0.7s linear infinite;
    }

    @keyframes forum-create-spin {
        to {
            transform: rotate(360deg);
        }
    }

    .dashboard.dark .forum-create-modal {
        background: #1e2128;
    }

    .dashboard.dark .forum-create-modal__header h3 {
        color: #f1f3f7;
    }

    .dashboard.dark .forum-create-modal__footer {
        border-top-color: #353945;
    }

    .dashboard.dark .forum-create-modal__btn--cancel {
        color: #f1f3f7;
        background: #353945;
    }
</style>
